/**
 * Çevrimiçi (presence) tespiti.
 *
 * Eski yöntem bir *durum bayrağıydı*: `loginTime` yazılır, çıkışta `logoutTime`
 * yazılırdı ve "çevrimiçi" = logoutTime yok ya da loginTime'dan eski demekti.
 * Bu yöntem çıkış yazısının her seferinde diske düşmesine bağlıydı ve düşmediği
 * çok durum vardı: sekme kapatma (beforeunload içindeki async yazı tamamlanmaz),
 * tarayıcı çökmesi, ağ kopması, bilgisayarın uyku moduna girmesi. Bunların
 * hepsinde kullanıcı sonsuza dek "çevrimiçi" görünüyordu.
 *
 * Yeni yöntem bir *nabız* (heartbeat): istemci açıkken belirli aralıklarla
 * `lastSeen` damgasını tazeler. Çevrimiçi olmak için son nabzın TTL içinde
 * olması yeterlidir. Çıkışta hiçbir şey yazılmasa bile kayıt kendiliğinden
 * eskir — yani sistem kendi kendini onarır.
 */

/** İstemcinin `lastSeen` tazeleme aralığı. */
export const HEARTBEAT_INTERVAL_MS = 30 * 1000;

/**
 * Bir nabzın "taze" sayıldığı süre. Kaçan tek bir nabzın kullanıcıyı anında
 * çevrimdışı göstermemesi için aralığın 2.5 katı seçildi.
 *
 * Bu süre aynı zamanda EN KÖTÜ durumda çevrimdışına düşme gecikmesidir:
 * tarayıcı çökerse ya da `pagehide` yazısı yetişmezse kullanıcı en geç bu kadar
 * sonra listeden düşer. Kısaltmak tespiti hızlandırır ama yazma maliyetini
 * artırır (her kullanıcı dakikada 60000/HEARTBEAT_INTERVAL_MS yazar).
 */
export const ONLINE_TTL_MS = 75 * 1000;

/** Firestore Timestamp / Date / ISO string → milisaniye. */
export const toMillis = (value) => {
  if (!value) return null;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return isNaN(parsed) ? null : parsed;
};

/**
 * Çalışan şu anda çevrimiçi mi?
 *
 * `now` dışarıdan verilebilir; böylece bir listedeki herkes tek bir zaman
 * referansına göre değerlendirilir ve sayfa saniyede bir yeniden hesaplanırken
 * tutarlı kalır.
 */
export const isEmployeeOnline = (employee, now = Date.now()) => {
  const lastSeen = toMillis(employee?.lastSeen);
  if (!lastSeen) return false;

  // Açık çıkış (Çıkış Yap) nabızdan sonra geldiyse kullanıcı TTL'i beklemeden
  // anında çevrimdışı olur.
  const logoutTime = toMillis(employee?.logoutTime);
  if (logoutTime && logoutTime > lastSeen) return false;

  return now - lastSeen <= ONLINE_TTL_MS;
};

/** Çevrimiçi çalışanların id kümesi. */
export const getOnlineEmployeeIds = (employees = [], now = Date.now()) => {
  const online = new Set();
  employees.forEach((employee) => {
    if (isEmployeeOnline(employee, now)) online.add(employee.id);
  });
  return online;
};
